import {
  Component,
  OnInit,
  ViewChild,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from 'src/app/app.service';
import { tap } from 'rxjs/operators';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { ApiRequestService } from '../api-request.service';
import { RBACService } from '../rbac.service';

@Component({
  selector: 'app-networked-accounts-selector',
  templateUrl: './networked-accounts-selector.component.html',
  styleUrls: ['./networked-accounts-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NetworkedAccountsSelectorComponent implements OnInit {
  // Columns to show in the view
  displayedColumns: string[] = ['name', 'date_created'];

  // The data source
  dataSource: NetworkedAccountsSelectorDataSource;

  // The paginator and sorter objects
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    public api: ApiRequestService,
    public dialogRef: MatDialogRef<NetworkedAccountsSelectorComponent>,
    public apiRequest: ApiRequestService,
    private rbac: RBACService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Set multiple if it is not present and default it to false
    if (
      typeof this.data['multiple'] === 'undefined' ||
      typeof this.data['multiple'] !== 'boolean'
    ) {
      this.data['multiple'] = false;
    }

    // set can clear and close if it is not present to false
    if (typeof this.data['can_clear_close'] === 'undefined') {
      this.data['can_clear_close'] = false;
    }

    if (this.data['multiple']) {
      this.displayedColumns.unshift('select');
    } else {
      this.displayedColumns.push('actions');
    }

    // Construct the datasource
    this.dataSource = new NetworkedAccountsSelectorDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // Set a default selection if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // Loop and select the values in the data source
    for (let i = 0; i < this.data['selected'].length; i++) {
      this.dataSource.selection.select(this.data['selected'][i]);
    }
  }

  ngOnInit() {
    // Check RBAC permission: standalone networked-accounts:view OR a parent feature that needs
    // company selection. Evaluated with exact: false, so any one of these is enough.
    //
    // The list is the union of two sets: the features whose screens open this selector directly,
    // and the Networked Users Selector list, because that selector embeds this one as its "change
    // company" filter. Narrowing it breaks the feature that needs the selector.
    //
    // MUST stay in sync with AccountController::getNetworkedAccounts().
    if (
      !this.rbac.userCanAccess(
        [
          // Standalone permission for the network lookup itself.
          RBACService.getPermission(
            RBACService.RBAC_F.F_NETWORKED_ACCOUNTS,
            RBACService.RBAC_A.A_VIEW
          ),
          // Networked Users Selector embeds this selector as its company filter.
          RBACService.getPermission(
            RBACService.RBAC_F.F_NETWORKED_USERS,
            RBACService.RBAC_A.A_VIEW
          ),
          // Safety Observations: edit dialog (contractor field) and list filters.
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_SAFETY_OBSERVATIONS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_SAFETY_OBSERVATIONS,
            RBACService.RBAC_A.A_EDIT
          ),
          // Incidents.
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INCIDENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INCIDENTS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INCIDENTS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_INCIDENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_INCIDENTS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_INCIDENTS,
            RBACService.RBAC_A.A_EDIT
          ),
          // Toolbox Talks. The register declares no create action, so it contributes view and edit only.
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TOOLBOX_TALKS,
            RBACService.RBAC_A.A_EDIT
          ),
          // TA/JSA/SWMS.
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_TA_JSA_SWMS,
            RBACService.RBAC_A.A_EDIT
          ),
          // Inspections & Audits: contractor selection on the audit edit screens.
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INSPECTIONS_AUDITS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_INSPECTIONS_AUDITS,
            RBACService.RBAC_A.A_EDIT
          ),
          // Tasks.
          RBACService.getPermission(
            RBACService.RBAC_F.F_TASKS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TASKS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TASKS,
            RBACService.RBAC_A.A_EDIT
          ),
          // Forms: assigned users filter.
          RBACService.getPermission(
            RBACService.RBAC_F.F_FORMS,
            RBACService.RBAC_A.A_VIEW
          ),
          // Safety Plans: list filter.
          RBACService.getPermission(
            RBACService.RBAC_F.F_SAFETY_PLANS,
            RBACService.RBAC_A.A_VIEW
          ),
          // Contractor Pre-Qualifications: pre-qualification view screen.
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS,
            RBACService.RBAC_A.A_EDIT
          ),
          // Inductions: induction report filter.
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
            RBACService.RBAC_A.A_VIEW
          ),
          // Site access reports: access, onsite, time onsite and site declaration filters, plus the
          // manage time onsite record dialog.
          RBACService.getPermission(
            RBACService.RBAC_F.F_REPORTS_SITE_ACCESS,
            RBACService.RBAC_A.A_VIEW
          ),
          // Messages: recipient company selection.
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_EDIT
          ),
          // Sites: Users Worked On, and EEP safety manager / first aider selection.
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_USERS_WORKED_ON,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_EDIT
          ),
          // Assets create/edit (form field selectors: the contractor that owns an asset, and the
          // contractors it is shared with).
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_ASSETS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_ASSETS,
            RBACService.RBAC_A.A_EDIT
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_ASSETS,
            RBACService.RBAC_A.A_CREATE
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_ASSETS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        false
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view networked accounts.'
      );
      this.dialogRef.close();
      return;
    }

    // Get the data. Deliberately here rather than in the constructor so the permission check above
    // always runs before the request is made.
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onSelectSingle(id: number) {
    this.dialogRef.close(id);
  }

  onSelectMultiple() {
    this.dialogRef.close(this.dataSource.selection.selected);
  }

  onSelectAllRecords() {
    this.apiRequest
      .makeRequest(
        'get',
        `v1/account/all-networked-accountids`,
        {},
        {
          search: this.dataSource.search
        }
      )
      .then((res) => {
        this.dialogRef.close(res);
      });
  }

  onDeselectAllRecords() {
    this.dataSource.selection.clear();
  }

  /**
   * Clear the current selection and close the dialog.
   *
   * @returns {void}
   */
  onClearSelectedAndClose(): void {
    this.dataSource.selection.clear();
    this.dialogRef.close(0);
  }

  onClose() {
    this.dialogRef.close();
  }
}

/**
 * The networked accounts selector data source.
 */
export class NetworkedAccountsSelectorDataSource extends CustomDataSource {
  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('account/networked-accounts', resetOffset);
  }
}
